import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";

export default function RecordsPage() {
  const navigate = useNavigate();
  const [sideOpen, setSideOpen] = useState(true);
  const [health, setHealth] = useState(null);
  const [results, setResults] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHealth, setEditingHealth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ blood_type:"", weight:"", height:"", bmi:"", allergies:[], conditions:[], emergency_contact:"" });
  const [newAllergy, setNewAllergy] = useState("");
  const [newCondition, setNewCondition] = useState("");
  const W = sideOpen ? "210px" : "50px";

  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) { navigate("/login"); return; }
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await api.get("records/");
      setHealth(res.data.health);
      setResults(res.data.results ?? []);
      setPrescriptions(res.data.prescriptions ?? []);
      if (res.data.health) {
        setForm({
          blood_type: res.data.health.blood_type || "",
          weight: res.data.health.weight || "",
          height: res.data.health.height || "",
          bmi: res.data.health.bmi || "",
          allergies: res.data.health.allergies || [],
          conditions: res.data.health.conditions || [],
          emergency_contact: res.data.health.emergency_contact || "",
        });
      }
    } catch {
      setHealth({ blood_type:"A+", weight:"175 lbs", height:"5'10\"", bmi:"25.1", allergies:["Penicillin"], conditions:["Hypertension"], emergency_contact:"Jane Doe — +1 (555) 000-0001" });
      setResults([
        { id:1, name:"CBC", category:"hematology", date:"2026-02-28", status:"completed", doctor:"Dr. Sarah Johnson", flagged:false },
        { id:2, name:"Lipid Panel", category:"chemistry", date:"2026-02-25", status:"completed", doctor:"Dr. Michael Chen", flagged:true },
      ]);
      setPrescriptions([
        { id:1, name:"Lisinopril 10mg", dose:"Once daily", refills:3, prescribed_by:"Dr. Sarah Johnson", active:true },
        { id:2, name:"Metformin 500mg", dose:"Twice daily with meals", refills:0, prescribed_by:"Dr. Michael Chen", active:true },
      ]);
    } finally { setLoading(false); }
  };

  const handleSaveHealth = async () => {
    setSaving(true);
    try {
      await api.patch("health/", form);
      setEditingHealth(false);
      fetchRecords();
    } catch {} finally { setSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    navigate("/login");
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Inter,sans-serif", color:"#6b7280", fontSize:13 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:32, height:32, border:"2px solid #e5e7eb", borderTopColor:"#00897B", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto 12px" }}></div>
        Loading health records…
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; color: #111; font-size: 13px; -webkit-font-smoothing: antialiased; }
        button { font-family: inherit; cursor: pointer; }
        .app { display: flex; min-height: 100vh; }
        .sb { width:${W}; background:#00897B; display:flex; flex-direction:column; position:fixed; top:0; left:0; bottom:0; z-index:200; transition:width .2s; overflow:hidden; }
        .sb-logo { height:56px; display:flex; align-items:center; padding:0 13px; gap:9px; border-bottom:1px solid rgba(255,255,255,0.1); flex-shrink:0; }
        .sb-logo-box { width:28px; height:28px; border-radius:7px; background:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .sb-logo-name { font-size:13px; font-weight:700; color:#fff; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-logo-sub { font-size:9.5px; color:rgba(255,255,255,0.45); white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-nav { flex:1; padding:10px 7px; overflow-y:auto; scrollbar-width:none; }
        .sb-grp { font-size:9.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:rgba(255,255,255,0.3); padding:8px 8px 3px; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .nb { width:100%; display:flex; align-items:center; gap:8px; padding:8px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.5); font-size:12.5px; font-weight:500; text-align:left; white-space:nowrap; margin-bottom:1px; transition:background .1s, color .1s; }
        .nb:hover { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.9); }
        .nb.on { background:rgba(255,255,255,0.15); color:#fff; font-weight:600; }
        .nav-ic { flex-shrink:0; width:15px; height:15px; display:flex; align-items:center; justify-content:center; }
        .nav-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; white-space:nowrap; }
        .sb-foot { border-top:1px solid rgba(255,255,255,0.1); padding:8px 7px; flex-shrink:0; }
        .sb-user { display:flex; align-items:center; gap:8px; padding:7px 9px; }
        .sb-av { width:28px; height:28px; border-radius:50%; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; flex-shrink:0; }
        .sb-uinfo { opacity:${sideOpen?1:0}; transition:opacity .15s; overflow:hidden; }
        .sb-uname { font-size:12px; font-weight:600; color:#fff; white-space:nowrap; }
        .sb-urole { font-size:10px; color:rgba(255,255,255,0.35); white-space:nowrap; }
        .sb-out { width:100%; display:flex; align-items:center; gap:8px; padding:7px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.3); font-size:12px; margin-top:2px; transition:background .1s, color .1s; }
        .sb-out:hover { background:rgba(239,68,68,0.15); color:#fca5a5; }
        .sb-out-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .main { margin-left:${W}; flex:1; display:flex; flex-direction:column; transition:margin-left .2s; min-width:0; }
        .topbar { height:56px; background:#fff; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; padding:0 18px; position:sticky; top:0; z-index:100; flex-shrink:0; }
        .tb-l { display:flex; align-items:center; gap:10px; }
        .tb-toggle { width:30px; height:30px; border-radius:6px; border:1px solid #e5e7eb; background:none; display:flex; align-items:center; justify-content:center; color:#6b7280; transition:background .1s; }
        .tb-toggle:hover { background:#f3f4f6; }
        .tb-title { font-size:13.5px; font-weight:700; color:#0f172a; }
        .tb-sub { font-size:11px; color:#9ca3af; }
        .tb-r { display:flex; align-items:center; gap:7px; }
        .tb-badge { display:flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; background:#E0F2F1; font-size:10.5px; font-weight:600; color:#00897B; }
        .tb-av { width:30px; height:30px; border-radius:50%; background:#00897B; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }
        .content { padding:16px; flex:1; }
        .grid3 { display:grid; grid-template-columns:320px 1fr; gap:14px; align-items:start; }
        .card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; margin-bottom:12px; }
        .card-head { padding:14px 18px; border-bottom:1px solid #f3f4f6; display:flex; align-items:center; justify-content:space-between; }
        .card-title { font-size:13px; font-weight:700; color:#0f172a; }
        .card-body { padding:16px 18px; }
        .vitals { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:14px; }
        .vital { background:#f8fafc; border:1px solid #e5e7eb; border-radius:8px; padding:10px 12px; }
        .vital-l { font-size:9.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#9ca3af; margin-bottom:3px; }
        .vital-v { font-size:18px; font-weight:700; color:#0f172a; }
        .sec-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:#9ca3af; margin:10px 0 6px; }
        .tags { display:flex; flex-wrap:wrap; gap:5px; }
        .tag { font-size:11px; font-weight:500; padding:3px 9px; border-radius:20px; }
        .tag-r { background:#fef2f2; color:#dc2626; }
        .tag-p { background:#f5f3ff; color:#7c3aed; }
        .info-row { display:flex; justify-content:space-between; padding:9px 0; border-bottom:1px solid #f3f4f6; }
        .info-row:last-child { border-bottom:none; }
        .info-lbl { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#9ca3af; }
        .info-val { font-size:12.5px; font-weight:500; color:#0f172a; text-align:right; }
        .btn-edit { padding:5px 12px; border-radius:6px; border:1px solid #e5e7eb; background:#fff; font-size:11.5px; font-weight:600; color:#00897B; }
        .btn-edit:hover { background:#E0F2F1; }
        .btn-save { padding:5px 12px; border-radius:6px; border:none; background:#00897B; font-size:11.5px; font-weight:600; color:#fff; }
        .btn-save:hover { background:#00695C; }
        .btn-cancel { padding:5px 12px; border-radius:6px; border:1px solid #e5e7eb; background:#f8fafc; font-size:11.5px; color:#64748b; }
        .field { margin-bottom:10px; }
        .field label { display:block; font-size:11px; font-weight:500; color:#475569; margin-bottom:4px; }
        .field input { width:100%; height:36px; padding:0 10px; font-size:12px; border:1px solid #e2e8f0; border-radius:7px; background:#f8fafc; outline:none; }
        .field input:focus { border-color:#00897B; background:#fff; }
        .grid2f { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .tag-input { display:flex; gap:6px; }
        .tag-input input { flex:1; height:32px; padding:0 10px; font-size:12px; border:1px solid #e2e8f0; border-radius:7px; background:#f8fafc; outline:none; }
        .tag-input input:focus { border-color:#00897B; background:#fff; }
        .tag-add { padding:0 10px; height:32px; border-radius:7px; border:none; background:#00897B; color:#fff; font-size:12px; font-weight:600; }
        .tag-rem { margin-left:4px; background:none; border:none; font-size:13px; color:inherit; opacity:.6; cursor:pointer; }
        .res-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid #f3f4f6; }
        .res-row:last-child { border-bottom:none; }
        .rdot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
        .dg { background:#16a34a; } .dy { background:#ca8a04; }
        .res-name { font-size:12.5px; font-weight:600; color:#0f172a; }
        .res-meta { font-size:11px; color:#9ca3af; }
        .rx-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid #f3f4f6; }
        .rx-row:last-child { border-bottom:none; }
        .rx-bar { width:3px; border-radius:2px; align-self:stretch; min-height:24px; flex-shrink:0; }
        .rx-name { font-size:12.5px; font-weight:600; color:#0f172a; margin-bottom:1px; }
        .rx-dose { font-size:11px; color:#6b7280; }
        .rx-tag { font-size:10px; font-weight:600; padding:2px 7px; border-radius:20px; white-space:nowrap; }
        .tg { background:#f0fdf4; color:#16a34a; }
        .ty { background:#fefce8; color:#ca8a04; }
        .tr { background:#fef2f2; color:#dc2626; }
        .spinner { width:12px; height:12px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; display:inline-block; margin-right:4px; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @media (max-width:900px) { .grid3 { grid-template-columns:1fr; } }
        @media (max-width:600px) { .content { padding:10px; } .grid2f { grid-template-columns:1fr; } }
      `}</style>

      <div className="app">
        <aside className="sb">
          <div className="sb-logo">
            <div className="sb-logo-box">
              <img src="/micronlabs.png" alt="MicronLabs" style={{ width:"18px", height:"18px", objectFit:"contain" }}/>
            </div>
            <div>
              <div className="sb-logo-name">MicronLabs</div>
              <div className="sb-logo-sub">Laboratory Information System</div>
            </div>
          </div>
          <nav className="sb-nav">
            <div className="sb-grp">Main</div>
            {[
              { id:"dashboard",label:"Dashboard",icon:<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
              { id:"requisitions",label:"E-Requisitions",icon:<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></> },
              { id:"results",label:"Lab Results",icon:<><path d="M9 3h6M9 3v8l-4 9h14l-4-9V3"/></> },
              { id:"prescriptions",label:"Prescriptions",icon:<><path d="M10.5 20H4a2 2 0 01-2-2v-3.5a2 2 0 012-2h6.5"/><path d="M13.5 4H20a2 2 0 012 2v3.5a2 2 0 01-2 2h-6.5"/><path d="M10.5 20l9-16"/></> },
              { id:"records",label:"Health Records",icon:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className={`nb ${id==="records"?"on":""}`} onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
            <div className="sb-grp" style={{marginTop:8}}>Account</div>
            {[
              { id:"messages",label:"Messages",icon:<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></> },
              { id:"billing",label:"Billing",icon:<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></> },
              { id:"profile",label:"My Profile",icon:<><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className="nb" onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
          </nav>
          <div className="sb-foot">
            <div className="sb-user">
              <div className="sb-av">U</div>
              <div className="sb-uinfo"><div className="sb-uname">My Account</div><div className="sb-urole">Patient</div></div>
            </div>
            <button className="sb-out" onClick={handleLogout}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              <span className="sb-out-lbl">Sign out</span>
            </button>
          </div>
        </aside>

        <div className="main">
          <header className="topbar">
            <div className="tb-l">
              <button className="tb-toggle" onClick={() => setSideOpen(o => !o)}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
              <div>
                <div className="tb-title">Health Records</div>
                <div className="tb-sub">{new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}</div>
              </div>
            </div>
            <div className="tb-r">
              <div className="tb-badge">
                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>
                HIPAA secured
              </div>
              <div className="tb-av">U</div>
            </div>
          </header>

          <div className="content">
            <div className="grid3">

              {/* LEFT — Health summary */}
              <div>
                <div className="card">
                  <div className="card-head">
                    <span className="card-title">Health summary</span>
                    {!editingHealth
                      ? <button className="btn-edit" onClick={() => setEditingHealth(true)}>Edit</button>
                      : <div style={{display:"flex",gap:6}}>
                          <button className="btn-cancel" onClick={() => setEditingHealth(false)}>Cancel</button>
                          <button className="btn-save" onClick={handleSaveHealth} disabled={saving}>
                            {saving && <span className="spinner"/>}Save
                          </button>
                        </div>
                    }
                  </div>
                  <div className="card-body">
                    {!editingHealth ? (
                      health ? (
                        <>
                          <div className="vitals">
                            {[["Blood type", health.blood_type],["BMI",health.bmi],["Weight",health.weight],["Height",health.height]].map(([l,v])=>(
                              <div key={l} className="vital"><div className="vital-l">{l}</div><div className="vital-v">{v||"—"}</div></div>
                            ))}
                          </div>
                          {health.allergies?.length > 0 && <>
                            <div className="sec-lbl">Allergies</div>
                            <div className="tags">{health.allergies.map((a,i)=><span key={i} className="tag tag-r">{a}</span>)}</div>
                          </>}
                          {health.conditions?.length > 0 && <>
                            <div className="sec-lbl">Diagnoses</div>
                            <div className="tags">{health.conditions.map((c,i)=><span key={i} className="tag tag-p">{c}</span>)}</div>
                          </>}
                          {health.emergency_contact && <>
                            <div className="sec-lbl">Emergency contact</div>
                            <div style={{fontSize:12,fontWeight:600,color:"#0f172a"}}>{health.emergency_contact}</div>
                          </>}
                        </>
                      ) : <div style={{color:"#9ca3af",fontSize:12,textAlign:"center",padding:"20px 0"}}>No health data on file</div>
                    ) : (
                      <>
                        <div className="grid2f">
                          <div className="field"><label>Blood type</label><input value={form.blood_type} onChange={e=>setForm({...form,blood_type:e.target.value})} placeholder="e.g. A+"/></div>
                          <div className="field"><label>BMI</label><input value={form.bmi} onChange={e=>setForm({...form,bmi:e.target.value})} placeholder="e.g. 24.5"/></div>
                          <div className="field"><label>Weight</label><input value={form.weight} onChange={e=>setForm({...form,weight:e.target.value})} placeholder="e.g. 170 lbs"/></div>
                          <div className="field"><label>Height</label><input value={form.height} onChange={e=>setForm({...form,height:e.target.value})} placeholder="e.g. 5'9&quot;"/></div>
                        </div>
                        <div className="field">
                          <label>Emergency contact</label>
                          <input value={form.emergency_contact} onChange={e=>setForm({...form,emergency_contact:e.target.value})} placeholder="Name — phone number"/>
                        </div>
                        <div className="field">
                          <label>Allergies</label>
                          <div className="tags" style={{marginBottom:6}}>
                            {form.allergies.map((a,i)=>(
                              <span key={i} className="tag tag-r">{a}<button className="tag-rem" onClick={()=>setForm({...form,allergies:form.allergies.filter((_,j)=>j!==i)})}>&times;</button></span>
                            ))}
                          </div>
                          <div className="tag-input">
                            <input value={newAllergy} onChange={e=>setNewAllergy(e.target.value)} placeholder="Add allergy" onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),newAllergy&&(setForm({...form,allergies:[...form.allergies,newAllergy]}),setNewAllergy("")))}/>
                            <button className="tag-add" type="button" onClick={()=>newAllergy&&(setForm({...form,allergies:[...form.allergies,newAllergy]}),setNewAllergy(""))}>Add</button>
                          </div>
                        </div>
                        <div className="field">
                          <label>Diagnoses / Conditions</label>
                          <div className="tags" style={{marginBottom:6}}>
                            {form.conditions.map((c,i)=>(
                              <span key={i} className="tag tag-p">{c}<button className="tag-rem" onClick={()=>setForm({...form,conditions:form.conditions.filter((_,j)=>j!==i)})}>&times;</button></span>
                            ))}
                          </div>
                          <div className="tag-input">
                            <input value={newCondition} onChange={e=>setNewCondition(e.target.value)} placeholder="Add condition" onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),newCondition&&(setForm({...form,conditions:[...form.conditions,newCondition]}),setNewCondition("")))}/>
                            <button className="tag-add" type="button" onClick={()=>newCondition&&(setForm({...form,conditions:[...form.conditions,newCondition]}),setNewCondition(""))}>Add</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT — Results and Prescriptions */}
              <div>
                <div className="card">
                  <div className="card-head">
                    <span className="card-title">Lab results history</span>
                    <button className="btn-edit" onClick={() => navigate("/results")}>View all</button>
                  </div>
                  <div className="card-body">
                    {results.length === 0
                      ? <div style={{color:"#9ca3af",fontSize:12,textAlign:"center",padding:"12px 0"}}>No lab results on file</div>
                      : results.map(r => (
                        <div key={r.id} className="res-row">
                          <div className={`rdot ${r.flagged?"dy":"dg"}`}/>
                          <div style={{flex:1}}>
                            <div className="res-name">{r.name}</div>
                            <div className="res-meta">{r.doctor} &nbsp;·&nbsp; {fmtDate(r.date)} &nbsp;·&nbsp; {r.category}</div>
                          </div>
                          <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,background:r.status==="completed"?"#f0fdf4":"#fefce8",color:r.status==="completed"?"#16a34a":"#ca8a04"}}>
                            {r.status}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <span className="card-title">Prescriptions</span>
                    <button className="btn-edit" onClick={() => navigate("/prescriptions")}>View all</button>
                  </div>
                  <div className="card-body">
                    {prescriptions.length === 0
                      ? <div style={{color:"#9ca3af",fontSize:12,textAlign:"center",padding:"12px 0"}}>No prescriptions on file</div>
                      : prescriptions.map(p => (
                        <div key={p.id} className="rx-row">
                          <div className="rx-bar" style={{background:p.refills===0?"#dc2626":p.refills<=1?"#ca8a04":"#16a34a"}}/>
                          <div style={{flex:1}}>
                            <div className="rx-name">{p.name}</div>
                            <div className="rx-dose">{p.dose}</div>
                          </div>
                          <span className={`rx-tag ${p.refills===0?"tr":p.refills<=1?"ty":"tg"}`}>
                            {p.refills===0?"Refill now":`${p.refills} refills`}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
